import { getActiveMoneyContext } from '@/utils/formatCurrency';
import type { CampaignAdProduct } from '@/types/spCampaigns';

export type BudgetPeriod = 'daily' | 'lifetime';
export type BudgetEntity = 'seller' | 'vendor';

type EntityMins = { seller: number; vendor: number };

function same(min: number): EntityMins {
  return { seller: min, vendor: min };
}

/** Amazon country code (or UK/UAE) → marketplace label used in the budget tables. */
const COUNTRY_MARKETPLACE: Record<string, string> = {
  AE: 'UAE',
  AU: 'AU',
  BE: 'BE',
  BR: 'BR',
  CA: 'CA',
  CN: 'CN',
  DE: 'DE',
  EG: 'EG',
  ES: 'ES',
  FR: 'FR',
  GB: 'UK',
  IE: 'IE',
  IN: 'IN',
  IT: 'IT',
  JP: 'JP',
  MX: 'MX',
  NL: 'NL',
  PL: 'PL',
  SA: 'SA',
  SE: 'SE',
  SG: 'SG',
  TR: 'TR',
  UAE: 'UAE',
  UK: 'UK',
  US: 'US',
  ZA: 'ZA',
};

/**
 * Minimum campaign budgets by marketplace.
 * https://advertising.amazon.com/API/docs/en-us/reference/concepts/limits
 */
const SP_MIN_DAILY: Record<string, number> = {
  AU: 1.4,
  BE: 1,
  BR: 1.32,
  CA: 1,
  CN: 1,
  DE: 1,
  EG: 7,
  ES: 1,
  FR: 1,
  IE: 1,
  IN: 50,
  IT: 1,
  JP: 100,
  MX: 1,
  NL: 1,
  PL: 2,
  SA: 4,
  SE: 9,
  SG: 1.39,
  TR: 2,
  UAE: 4,
  UK: 1,
  US: 1,
  ZA: 20,
};

/** Sponsored Brands daily minimums. https://advertising.amazon.com/API/docs/en-us/reference/concepts/limits */
const SB_MIN_DAILY: Record<string, EntityMins> = {
  AU: same(1.4),
  BE: same(1),
  BR: same(4),
  CA: same(1),
  CN: same(1),
  DE: same(1),
  EG: same(7),
  ES: same(1),
  FR: same(1),
  IE: same(1),
  IN: { seller: 100, vendor: 500 },
  IT: same(1),
  JP: same(100),
  MX: same(1),
  NL: same(4),
  PL: same(2),
  SA: same(4),
  SE: same(9),
  SG: same(4),
  TR: same(2),
  UAE: same(4),
  UK: same(1),
  US: same(1),
  ZA: same(6),
};

/** Sponsored Brands lifetime minimums. https://advertising.amazon.com/API/docs/en-us/reference/concepts/limits */
const SB_MIN_LIFETIME: Record<string, number> = {
  AU: 141,
  BE: 100,
  BR: 367,
  CA: 100,
  CN: 100,
  DE: 100,
  EG: 750,
  ES: 100,
  FR: 100,
  IE: 100,
  IN: 5000,
  IT: 100,
  JP: 10000,
  MX: 1000,
  NL: 367,
  PL: 200,
  SA: 367,
  SE: 900,
  SG: 367,
  TR: 250,
  UAE: 367,
  UK: 100,
  US: 100,
  ZA: 650,
};

/** Sponsored Display daily minimums. https://advertising.amazon.com/API/docs/en-us/reference/concepts/limits */
const SD_MIN_DAILY: Record<string, number> = {
  AU: 1.4,
  BE: 1,
  BR: 1.32,
  CA: 1,
  DE: 1,
  EG: 7,
  ES: 1,
  FR: 1,
  IN: 50,
  IT: 1,
  JP: 100,
  MX: 1,
  NL: 1,
  PL: 1,
  SA: 4,
  SE: 1,
  SG: 4,
  TR: 2,
  UAE: 4,
  UK: 1,
  US: 1,
  ZA: 6,
};

export function marketplaceFromCountry(countryCode: string | null | undefined): string | null {
  const code = (countryCode ?? '').trim().toUpperCase();
  if (!code) return null;
  return COUNTRY_MARKETPLACE[code] ?? null;
}

export function budgetEntity(accountType: string | null | undefined): BudgetEntity {
  const value = (accountType ?? '').trim().toLowerCase();
  if (value.includes('vendor')) return 'vendor';
  return 'seller';
}

export function budgetPeriodFromType(budgetType: string | null | undefined): BudgetPeriod {
  const value = (budgetType ?? '').trim().toUpperCase().replace(/[\s-]/g, '_');
  if (value === 'LIFETIME' || value === 'LIFE_TIME') return 'lifetime';
  return 'daily';
}

export function marketplaceBudgetMin(options: {
  countryCode?: string | null;
  accountType?: string | null;
  adProduct: CampaignAdProduct;
  period?: BudgetPeriod;
}): number | null {
  const marketplace = marketplaceFromCountry(options.countryCode);
  if (!marketplace) return null;
  if (options.adProduct === 'SB' && options.period === 'lifetime') {
    return SB_MIN_LIFETIME[marketplace] ?? null;
  }
  if (options.adProduct === 'SB') {
    return SB_MIN_DAILY[marketplace]?.[budgetEntity(options.accountType)] ?? null;
  }
  const table = options.adProduct === 'SD' ? SD_MIN_DAILY : SP_MIN_DAILY;
  return table[marketplace] ?? null;
}

export function activeMarketplaceBudgetMin(
  adProduct: CampaignAdProduct,
  period: BudgetPeriod = 'daily'
): number | null {
  const account = getActiveMoneyContext();
  return marketplaceBudgetMin({
    countryCode: account.countryCode,
    accountType: account.accountType,
    adProduct,
    period,
  });
}
